/** Shared image CDN hosts until Routellers has its own. */
export const IMAGE_CDN_HOST_COM = 'd2z3luly8bezbw.cloudfront.net';
export const IMAGE_CDN_HOST_RU = 'picture.escapenavigator.ru';

const S3_BUCKET_MARKER = 'escapenavigator-images.s3';
const CDN_HOSTS = [IMAGE_CDN_HOST_COM, IMAGE_CDN_HOST_RU];

/** Resize tokens used by the image CDN rewriter. */
export const imageSizes = {
    xs: '300x300',
    s: '300x300',
    m: '300x300',
    l: '300x300',
    xl: '300x300',
    xxl: '640x640',
    auto: '640x640',
} as const;

export type ImageResizeSize = keyof typeof imageSizes | (string & {});

export const GALLERY_IMAGE_SIZE = '1280x1280';

/** Layered walk/route poster cutout (wide banner with alpha). */
export const COVER_POSTER_IMAGE_SIZE = '2500x980';

/** Default CDN resize box when size is omitted. */
export const DEFAULT_IMAGE_DIMENSIONS = '300x300';

export const isRuImageRegion = (): boolean => process.env.REACT_APP_REGION === 'ru';

export const getImageCdnHost = (): string =>
    isRuImageRegion() ? IMAGE_CDN_HOST_RU : IMAGE_CDN_HOST_COM;

export const extractStoragePath = (src: string): string | null => {
    if (src.includes('amazonaws.com/')) {
        return src.split('amazonaws.com/')[1]?.split('?')[0] ?? null;
    }

    const match = src.match(/fit-in\/[^/]+\/(.+?)(?:\?|$)/);

    return match?.[1] ?? null;
};

export const isEscapenavigatorImage = (src: string): boolean =>
    src.includes(S3_BUCKET_MARKER) || CDN_HOSTS.some((host) => src.includes(host));

export const resolveImageDimensions = (size?: ImageResizeSize): string => {
    if (!size) return DEFAULT_IMAGE_DIMENSIONS;

    if (size in imageSizes) {
        return imageSizes[size as keyof typeof imageSizes];
    }

    return size;
};

const longestSide = (dimensions: string): number => {
    const sides = dimensions.split('x').map((part) => Number(part));
    const side = Math.max(...sides.filter((value) => Number.isFinite(value) && value > 0));

    return Number.isFinite(side) ? side : 300;
};

/**
 * Commons `Special:FilePath` is not in our bucket, so CloudFront cannot resize it.
 * `?width=` is the thumbnail the file page already serves.
 */
const withWikiWidth = (src: string, side: number): string | null => {
    if (!/\/\/(?:[\w.-]+\.)?(?:wikimedia|wikipedia)\.org\/wiki\/Special:FilePath\//i.test(src)) {
        return null;
    }

    const hashAt = src.indexOf('#');
    const hash = hashAt >= 0 ? src.slice(hashAt) : '';
    const withoutHash = hashAt >= 0 ? src.slice(0, hashAt) : src;
    const queryAt = withoutHash.indexOf('?');
    const path = queryAt >= 0 ? withoutHash.slice(0, queryAt) : withoutHash;
    const kept = (queryAt >= 0 ? withoutHash.slice(queryAt + 1) : '')
        .split('&')
        .filter((part) => part && !/^width=/i.test(part));

    kept.push(`width=${side}`);

    return `${path}?${kept.join('&')}${hash}`;
};

/**
 * @see en-ui getImageSrc — same CloudFront `/fit-in/` rewrite for our S3 files.
 * Wikimedia file-path URLs get a matching thumbnail width. Anything else is unchanged.
 */
export const getImageUrl = (src?: string, size?: ImageResizeSize): string | undefined => {
    if (
        !src ||
        src.startsWith('data:') ||
        src.startsWith('file:') ||
        src.startsWith('content:') ||
        src.startsWith('ph://') ||
        src.startsWith('assets-library:')
    ) {
        return src;
    }

    const dimensions = resolveImageDimensions(size);
    const wiki = withWikiWidth(src, longestSide(dimensions));

    if (wiki) return wiki;

    if (!isEscapenavigatorImage(src)) return src;

    const path = extractStoragePath(src);

    if (!path) return src;

    return `https://${getImageCdnHost()}/fit-in/${dimensions}/${path}`;
};

/** @deprecated use imageSizes */
export const imageResizeSizes = imageSizes;
